'use client';

import { getSupabaseClient } from '@popow/infra/lib/supabase/client';
import * as Sentry from '@sentry/nextjs';
import type { User } from '@supabase/supabase-js';
import { useEffect } from 'react';
import { useMyProfile } from '@/entities/user';
import { useIdentifyUserOnLoad } from '@/features/common/analytics/hooks/useIdentifyUserOnLoad';
import { useMixpanelAliasOnFirstSignup } from '@/features/common/analytics/hooks/useMixpanelAliasOnFirstSignup';
import { useAuth } from '@/shared/auth';

interface AuthStoreInitializerProps {
  initialUser?: User | null;
  children: React.ReactNode;
}

/**
 * Auth 초기화 및 Supabase auth 구독 관리
 *
 * 역할:
 * - Supabase auth listener 설정
 * - Sentry user tracking
 * - Analytics hooks (useIdentifyUserOnLoad, useMixpanelAliasOnFirstSignup)
 *
 * Profile 관리는 useMyProfile()이 처리합니다.
 */
export function AuthStoreInitializer({ initialUser, children }: AuthStoreInitializerProps) {
  const user = useAuth(s => s.user);
  const { profile } = useMyProfile(); // Profile 데이터 프리페치 + tiktok_sec_uid 접근용

  // Initialize auth subscription
  useEffect(() => {
    const { setUser } = useAuth.getState();

    // Set initial state
    setUser(initialUser ?? null);

    // Initialize Sentry with initial user (if exists)
    if (initialUser) {
      Sentry.setUser({ id: initialUser.id, email: initialUser.email });
    }

    const supabase = getSupabaseClient();

    // Subscribe to auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const newUser = session?.user ?? null;
      setUser(newUser);

      if (newUser) {
        // Sentry tracking
        Sentry.setUser({ id: newUser.id, email: newUser.email });
      } else {
        // Logout
        Sentry.setUser(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [initialUser]);

  // Analytics hooks - profile의 tiktok_sec_uid를 전달하여 sec_uid 우선 정책 적용
  useMixpanelAliasOnFirstSignup(user, profile?.tiktok_sec_uid);
  useIdentifyUserOnLoad(user, profile?.tiktok_sec_uid);

  return <>{children}</>;
}
